<template>
  <div class="share-page">
    <!-- 加载中 -->
    <div class="loading-state" v-if="loading">
      <van-icon name="loading" class="loading-icon" />
      <p>加载中...</p>
    </div>

    <!-- 链接无效 -->
    <div class="error-state" v-else-if="error">
      <van-icon name="warning-o" class="error-icon" />
      <p class="error-text">{{ error }}</p>
    </div>

    <!-- 正常显示 -->
    <div class="main-content" v-else>
      <div class="user-card">
        <div class="avatar">
          <van-icon name="user-o" size="36" color="#fff" />
        </div>
        <div class="user-info">
          <div class="user-name">{{ userInfo.name }}</div>
          <div class="user-school">{{ userInfo.school }}</div>
          <div class="user-class" v-if="userInfo.class_name">{{ userInfo.class_name }}</div>
        </div>
      </div>

      <div class="plan-card" v-if="userInfo.plan_name">
        <div class="plan-label">实习计划</div>
        <div class="plan-name">{{ userInfo.plan_name }}</div>
        <div class="plan-date" v-if="userInfo.plan_start">
          {{ userInfo.plan_start }} ~ {{ userInfo.plan_end }}
        </div>
      </div>

      <!-- 打卡按钮 -->
      <div class="sign-area">
        <button class="sign-btn" :class="{ signing: signing }" :disabled="signing" @click="doSign">
          {{ waitingForProxy ? '正在获取代理…' : (signing ? '打卡中...' : '立即打卡') }}
        </button>
        <p class="sign-tip" v-if="signResult">{{ signResult }}</p>
        <button class="retry-btn" v-if="showRetryBtn" @click="retrySign">确认验证成功，立即打卡</button>
      </div>

    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { Toast, Dialog } from 'vant';
import { API_ENDPOINTS, TIMEOUT_CONFIG } from '@/config/api';
import { createSignOperationManager, createSignPendingStore, isSameSignDay } from '@/utils/signOperation';
import { waitForManualProxy } from '@/utils/manualProxyWait';

const SHARE_ALIPAY_PENDING_KEY = 'share_sign_alipay_pending';
const ALIPAY_PENDING_TTL = 10 * 60 * 1000;
const WECHAT_ALIPAY_TIP = '微信无法跳转支付宝，请浏览器打开网站';

export default {
  name: 'ShareSign',
  data() {
    return {
      token: '',
      loading: true,
      error: '',
      signing: false,
      waitingForProxy: false,
      signResult: '',
      showRetryBtn: false,
      userInfo: {
        name: '',
        school: '',
        class_name: '',
        plan_name: '',
        plan_start: '',
        plan_end: '',
      },
    };
  },
  async mounted() {
    this._manualProxyWaitStopped = false;
    window.addEventListener('pagehide', this.stopManualProxyWaiting);
    window.addEventListener('pageshow', this.resumeManualProxyPage);
    this.token = this.$route.params.token;
    if (!this.token) {
      this.error = '链接无效';
      this.loading = false;
      return;
    }
    this.bindAlipayReturnEvents();
    await this.loadInfo();
    this.checkAlipayReturn();
  },
  beforeDestroy() {
    this.stopManualProxyWaiting();
    window.removeEventListener('pagehide', this.stopManualProxyWaiting);
    window.removeEventListener('pageshow', this.resumeManualProxyPage);
    this.unbindAlipayReturnEvents();
    if (this._alipayRetryTimer) {
      clearTimeout(this._alipayRetryTimer);
    }
  },
  methods: {
    stopManualProxyWaiting() {
      this._manualProxyWaitStopped = true;
      this._manualProxyWaitEpoch = (this._manualProxyWaitEpoch || 0) + 1;
    },
    resumeManualProxyPage() {
      this._manualProxyWaitStopped = false;
    },
    getSignOperationManager() {
      if (!this._signOperationManager) {
        this._signOperationManager = createSignOperationManager('share', ALIPAY_PENDING_TTL);
      }
      return this._signOperationManager;
    },

    isWeChatBrowser() {
      return /MicroMessenger/i.test(navigator.userAgent || '');
    },

    getShareAlipayPending() {
      var store = this.getShareAlipayPendingStore();
      try {
        var pending = store.read();
        if (!pending) return null;
        if (!pending || !pending.token || !pending.createdAt || !pending.operation) {
          store.clear();
          return null;
        }
        var operation = this.getSignOperationManager().read(pending.token);
        if (Date.now() - pending.createdAt > ALIPAY_PENDING_TTL || !isSameSignDay(pending.createdAt) ||
            !operation || operation.id !== pending.operation.id || ['success', 'failed'].includes(operation.state)) {
          store.clear();
          return null;
        }
        return pending;
      } catch (e) {
        store.clear();
        return null;
      }
    },

    getShareAlipayPendingStore() {
      if (!this._alipayPendingStore) this._alipayPendingStore = createSignPendingStore(SHARE_ALIPAY_PENDING_KEY);
      return this._alipayPendingStore;
    },

    saveShareAlipayPending(pending) {
      this.getShareAlipayPendingStore().save(pending);
    },

    clearShareAlipayPending(operation) {
      var pending = this.getShareAlipayPending();
      if (pending && operation && pending.operation.id !== operation.id) return;
      if (this._alipayRetryTimer) {
        clearTimeout(this._alipayRetryTimer);
        this._alipayRetryTimer = null;
      }
      this.getShareAlipayPendingStore().clear();
    },

    markAlipayPageLeft() {
      var pending = this.getShareAlipayPending();
      if (!pending || pending.token !== this.token || pending.retryConsumed || pending.leftPage) return;
      pending.leftPage = true;
      this.saveShareAlipayPending(pending);
    },

    bindAlipayReturnEvents() {
      this._onAlipayVisibilityChange = () => {
        if (document.visibilityState === 'hidden') {
          this.markAlipayPageLeft();
        } else {
          this.checkAlipayReturn();
        }
      };
      this._onAlipayPageBlur = () => this.markAlipayPageLeft();
      this._onAlipayPageHide = () => this.markAlipayPageLeft();
      this._onAlipayPageFocus = () => this.checkAlipayReturn();
      this._onAlipayPageShow = () => this.checkAlipayReturn();
      document.addEventListener('visibilitychange', this._onAlipayVisibilityChange);
      window.addEventListener('blur', this._onAlipayPageBlur);
      window.addEventListener('pagehide', this._onAlipayPageHide);
      window.addEventListener('focus', this._onAlipayPageFocus);
      window.addEventListener('pageshow', this._onAlipayPageShow);
    },

    unbindAlipayReturnEvents() {
      document.removeEventListener('visibilitychange', this._onAlipayVisibilityChange);
      window.removeEventListener('blur', this._onAlipayPageBlur);
      window.removeEventListener('pagehide', this._onAlipayPageHide);
      window.removeEventListener('focus', this._onAlipayPageFocus);
      window.removeEventListener('pageshow', this._onAlipayPageShow);
    },

    checkAlipayReturn() {
      var pending = this.getShareAlipayPending();
      if (!pending || pending.token !== this.token || pending.retryConsumed || !pending.leftPage) return;
      if (document.visibilityState === 'hidden' || this._alipayAutoRetryRunning) return;
      if (this._alipayRetryTimer) {
        clearTimeout(this._alipayRetryTimer);
        this._alipayRetryTimer = null;
      }
      this._alipayRetryTimer = setTimeout(() => this.retryShareSignAfterAlipay(pending.operation), 1800);
    },

    async retryShareSignAfterAlipay(expectedOperation) {
      var pending = this.getShareAlipayPending();
      if (!pending || pending.token !== this.token || pending.retryConsumed || !pending.leftPage || this._alipayAutoRetryRunning) return;
      if (expectedOperation && (expectedOperation.id !== pending.operation.id || expectedOperation.step !== pending.operation.step)) return;
      if (!this.getSignOperationManager().matches(this.token, pending.operation) || this.signing) return;

      // Consume the queued callback before starting the single continuation.
      if (this._alipayRetryTimer) {
        clearTimeout(this._alipayRetryTimer);
        this._alipayRetryTimer = null;
      }
      if (document.visibilityState === 'hidden') return;

      this._alipayAutoRetryRunning = true;
      try {
        const current = this.getShareAlipayPending();
        if (!current || current.token !== this.token || current.retryConsumed ||
            current.operation.id !== pending.operation.id || current.operation.step !== pending.operation.step ||
            !this.getSignOperationManager().matches(this.token, pending.operation) || this.signing) return;
        current.retryConsumed = true;
        this.saveShareAlipayPending(current);
        this.signResult = '正在继续提交本次打卡...';
        this.showRetryBtn = false;
        await this.doSign({ isAlipayAutoRetry: true, operation: pending.operation });
      } finally {
        this._alipayAutoRetryRunning = false;
      }
    },

    formatTime(t) {
      if (!t) return '';
      var s = String(t);
      if (s.length >= 5) return s.substring(0, 5);
      return s;
    },

    async loadInfo() {
      try {
        var response = await axios.get(API_ENDPOINTS.PUB.SHARE_INFO, {
          params: { token: this.token },
          timeout: TIMEOUT_CONFIG.DEFAULT,
        });
        if (response.data.code === 0) {
          this.userInfo = response.data.data;
        } else {
          this.error = response.data.message || '链接无效';
        }
      } catch (e) {
        this.error = '网络错误，请稍后重试';
      } finally {
        this.loading = false;
      }
    },

    async doSign(options) {
      if (this.signing) return 'busy';
      var isAlipayAutoRetry = Boolean(options && options.isAlipayAutoRetry);
      var manager = this.getSignOperationManager();
      var operation = manager.begin(this.token, options && options.operation);
      if (!operation) return 'stale';
      var proxyWaitEpoch = this._manualProxyWaitEpoch || 0;
      this.signing = true;
      var pending = this.getShareAlipayPending();
      if (pending && pending.operation.id === operation.id) {
        pending.operation = operation;
        pending.retryConsumed = true;
        this.saveShareAlipayPending(pending);
        if (this._alipayRetryTimer) clearTimeout(this._alipayRetryTimer);
      }
      this.signResult = '';
      this.showRetryBtn = false;
      try {
        var request = () => axios.post(
          API_ENDPOINTS.PUB.SHARE_SIGN,
          { token: this.token, operation_id: operation.id, operation_step: operation.step },
          { timeout: TIMEOUT_CONFIG.GXY_MANUAL_SIGN }
        );
        var response = await waitForManualProxy(request, operation, {
          isCurrent: () => !this._manualProxyWaitStopped &&
            (this._manualProxyWaitEpoch || 0) === proxyWaitEpoch && manager.matches(this.token, operation),
          onWait: () => {
            manager.mark(this.token, operation, 'waiting_proxy');
            this.waitingForProxy = true;
            this.signResult = '正在获取代理，请保持页面打开';
          },
        });
        if (!response) return 'stale';

        var responseData = response.data || {};
        var verifyData =
          responseData.type === 'alipay_verify'
            ? responseData
            : responseData.data;

        if (verifyData && verifyData.type === 'alipay_verify') {
          manager.mark(this.token, operation, 'awaiting_auth');
          if (isAlipayAutoRetry) {
            this.signResult = '认证状态暂未生效，请稍后手动再打卡一次';
            this.showRetryBtn = true;
            Toast('认证状态暂未生效');
            return 'alipay_verify';
          }
          this.handleAlipayRedirect(verifyData, operation);
          return 'alipay_verify';
        }

        if (responseData.code === 0 || responseData.code === 30010) {
          manager.mark(this.token, operation, 'success');
          this.clearShareAlipayPending(operation);
          this.signResult = responseData.code === 30010 ? (responseData.message || '该类型已打卡') : '打卡成功';
          Toast.success(this.signResult);
          return 'success';
        } else if (responseData.code === 20003) {
          manager.mark(this.token, operation, 'failed');
          this.clearShareAlipayPending(operation);
          this.signResult = '签到权限已过期，请联系管理员续费';
          Toast.fail('权限已过期');
        } else {
          manager.mark(this.token, operation, responseData.code === 30015 ? 'retryable' : 'failed');
          if (responseData.code !== 30015) this.clearShareAlipayPending(operation);
          this.signResult = responseData.message || '打卡失败';
          Toast.fail(responseData.message || '打卡失败');
          if (isAlipayAutoRetry && responseData.code !== 30016) this.showRetryBtn = true;
          return responseData.code === 30015 ? 'busy' : 'failed';
        }
      } catch (e) {
        manager.mark(this.token, operation, 'retryable');
        this.signResult = '打卡结果暂未确认，请先查询打卡记录';
        Toast(this.signResult);
        if (isAlipayAutoRetry) this.showRetryBtn = true;
        return 'network_error';
      } finally {
        this.waitingForProxy = false;
        this.signing = false;
      }
    },

    retrySign() {
      this.showRetryBtn = false;
      this.doSign();
    },

    handleAlipayRedirect(data, operation) {
      var manager = this.getSignOperationManager();
      if (!manager.matches(this.token, operation) || manager.read(this.token).state !== 'awaiting_auth') return;
      var url = data.url || '';
      if (this.isWeChatBrowser()) {
        this.clearShareAlipayPending(operation);
        this.signResult = WECHAT_ALIPAY_TIP;
        this.showRetryBtn = false;
        Dialog.alert({
          title: '无法跳转支付宝',
          message: WECHAT_ALIPAY_TIP,
          confirmButtonText: '我知道了',
        }).catch(() => {});
        return;
      }
      if (url) {
        this.clearShareAlipayPending();
        this.signResult = '认证完成返回本页后将自动继续打卡';
        this.saveShareAlipayPending({
          token: this.token,
          operation,
          createdAt: Date.now(),
          leftPage: false,
          retryConsumed: false,
        });
        if (url.startsWith('alipays://') || url.startsWith('alipay://')) {
          var a = document.createElement('a');
          a.href = url;
          a.setAttribute('target', '_blank');
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        } else {
          window.location.href = url;
        }
      } else {
        this.signResult = data.tip || '需要完成支付宝人脸认证';
        Toast(data.tip || '需要支付宝认证');
      }
    },
  },
};
</script>

<style scoped>
.share-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #e8f5e9 0%, #f1f8e9 100%);
  padding: 40px 20px;
  box-sizing: border-box;
}

.loading-state,
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  color: #999;
}

.loading-icon {
  font-size: 40px;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.error-icon {
  font-size: 60px;
  color: #ee0a24;
  margin-bottom: 16px;
}

.error-text {
  font-size: 16px;
  color: #666;
}

.main-content {
  max-width: 400px;
  margin: 0 auto;
}

.user-card {
  background: #fff;
  border-radius: 16px;
  padding: 24px;
  display: flex;
  align-items: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  margin-bottom: 16px;
}

.avatar {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: #00CB6A;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-right: 16px;
}

.user-info {
  flex: 1;
  overflow: hidden;
}

.user-name {
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
}

.user-school {
  font-size: 14px;
  color: #666;
  margin-bottom: 2px;
}

.user-class {
  font-size: 13px;
  color: #999;
}

.plan-card {
  background: #fff;
  border-radius: 16px;
  padding: 20px 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  margin-bottom: 16px;
}

.plan-label {
  font-size: 12px;
  color: #00CB6A;
  font-weight: 500;
  margin-bottom: 8px;
}

.plan-name {
  font-size: 15px;
  color: #333;
  margin-bottom: 4px;
}

.plan-date {
  font-size: 13px;
  color: #999;
}

.sign-area {
  margin: 30px 0;
  text-align: center;
}

.sign-btn {
  width: 140px;
  height: 140px;
  border-radius: 50%;
  border: none;
  background: linear-gradient(135deg, #00CB6A 0%, #00a857 100%);
  color: #fff;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 8px 30px rgba(0, 203, 106, 0.35);
  transition: all 0.3s ease;
  letter-spacing: 2px;
}

.sign-btn:active {
  transform: scale(0.95);
  box-shadow: 0 4px 15px rgba(0, 203, 106, 0.3);
}

.sign-btn.signing {
  background: linear-gradient(135deg, #b0bec5 0%, #90a4ae 100%);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  cursor: not-allowed;
}

.sign-tip {
  margin-top: 16px;
  font-size: 14px;
  color: #666;
  padding: 10px 16px;
  background: #fff;
  border-radius: 12px;
  display: inline-block;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.04);
}

.retry-btn {
  margin-top: 16px;
  width: 80%;
  max-width: 280px;
  height: 44px;
  border: none;
  border-radius: 22px;
  background: #1989fa;
  color: #fff;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
}

.retry-btn:active {
  background: #1478de;
}
</style>
